/**
 * Mirrors every workspace to a managed folder on disk as a REAL nested tree, so
 * the on-disk layout matches the sidebar tree 1:1:
 *
 *   {root}/
 *     apiboo.yaml                      workspace manifest
 *     variables/globals.yaml
 *     variables/workspace.yaml
 *     environments/{env}.yaml
 *     {collection}/                    each collection is a top-level folder
 *       .collection.yaml               collection metadata (id, settings, …)
 *       {request}.yaml                 a request at the collection root
 *       {folder}/                      each folder is a real nested directory
 *         .folder.yaml                 folder metadata (id, name, sort_order)
 *         {request}.yaml
 *         {nested-folder}/ …
 *
 * This reads the in-memory snapshot only — it never touches the sidebar tree or
 * DnD logic. The flat parent_id / folder_id / sort_order model is reconstructed
 * faithfully on read (folder_id is derived from a request's directory; folder
 * parent_id from its directory nesting).
 *
 * Mirror lives under the CURRENT ACCOUNT'S data root:
 *   <AppLocalData>/users/<accountId>/workspace-mirror/{workspaceId}/
 *
 * Every workspace is mirrored, cloud ones included, as plaintext YAML — so
 * before it was account-scoped this folder was one account's collections,
 * headers and variables sitting in a directory the next account could read. The
 * account root is now part of every path built here, and sign-out deletes that
 * root, vault aside (accountData.purgeAccountData). The mirror is a derived copy —
 * the snapshot in `local-workspaces/` is the source of truth — which is what
 * makes purging it on sign-out lossless.
 */
import yaml from 'js-yaml';
import { isTauri as coreIsTauri } from '@tauri-apps/api/core';
import { accountPath } from './accountData.js';
import { normalizeSnapshot } from './localWorkspaceStore.js';
import { parseYaml } from '../utils/yamlWorkspace/deterministicYaml.js';
import { slugify } from '../utils/yamlWorkspace/paths.js';
import { normalizeMock } from '../utils/workspaceTabPersistence.js';
import { requestExtraFields, REQUEST_DENIED_KEYS, withoutBlankRows } from '../utils/yamlWorkspace/serialize.js';
import { normalizeRequestBodyMode } from '../utils/requestBodyMode.js';
import { splitSecrets, mergeSecrets, sidecarOf, isSidecar } from './mirrorSecrets.js';
import { writeWorkspaceFiles, MANAGED_ROOTS } from './gitFileWriter.js';
import { GITIGNORE_TEMPLATE, GITATTRIBUTES_TEMPLATE } from '../utils/yamlWorkspace/schema.js';
import { SCHEMA_VERSION } from '../utils/yamlWorkspace/schema.js';
import { getOrderedChildren } from '../utils/collectionFolders.js';
import { normalizeFolderVariables } from '../utils/folderVariables.js';
import { getOrderedSchemaChildren } from '../utils/schemaTree.js';

/**
 * Dump YAML for the mirror with git-friendly formatting:
 *  - insertion key order (readable; deterministic because the writer is fixed)
 *  - lineWidth: -1 → never folds long lines (no wrap churn); multi-line strings
 *    (bodies/scripts) are emitted as `|` block scalars so they diff line-by-line
 */
function mdump(obj) {
  return yaml.dump(obj, {
    lineWidth: -1,
    noRefs: true,
    quotingType: '"',
    forceQuotes: false,
  });
}

const MIRROR_DIR = 'workspace-mirror';
// NOTE: not dotfiles — Tauri's fs scope glob (`**`) does not match hidden files,
// so writing `.collection.yaml` / `.folder.yaml` is denied. Use plain names and
// treat them as reserved within their directory.
const COLLECTION_META = 'collection.yaml';
const FOLDER_META = 'folder.yaml';
const RESERVED_NAMES = new Set([COLLECTION_META, FOLDER_META]);
const RESERVED_TOP_LEVEL = new Set(['variables', 'environments', 'schemas']);

function isTauri() {
  try {
    return coreIsTauri();
  } catch {
    return typeof window !== 'undefined' && window.__TAURI__ !== undefined;
  }
}

async function fsModule() {
  return import('@tauri-apps/plugin-fs');
}

/**
 * Absolute mirror dir inside the current account's root. Absolute because the
 * Rust git_sync commands (libgit2) need a real path — and because there is no
 * relative form any more: the account segment is not optional.
 * @returns {Promise<string|null>} null outside Tauri.
 */
export async function mirrorAbsDir(workspaceId) {
  return accountPath(`${MIRROR_DIR}/${String(workspaceId)}`);
}

function schemaNodeToObject(node) {
  const o = {
    id: node.id,
    name: node.name ?? '',
    type: node.type ?? 'string',
  };
  if (node.value) o.value = node.value;
  if (node.refSchemaId) o.ref_schema_id = node.refSchemaId;
  if (node.description) o.description = node.description;
  if (Array.isArray(node.children) && node.children.length) {
    o.children = node.children.map(schemaNodeToObject);
  }
  return o;
}

function schemaItemToObject(item) {
  return {
    id: item.id,
    name: item.name || 'Schema',
    root_type: item.rootType === 'array' ? 'array' : 'object',
    sort_order: typeof item.sort_order === 'number' ? item.sort_order : 0,
    nodes: (item.nodes || []).map(schemaNodeToObject),
  };
}

function readSchemaNode(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const type = raw.type ?? 'string';
  const hasChildren = type === 'object' || type === 'array';
  return {
    id: raw.id,
    name: raw.name ?? '',
    type,
    value: typeof raw.value === 'string' ? raw.value : '',
    refSchemaId: raw.ref_schema_id ?? raw.refSchemaId ?? null,
    description: raw.description ?? '',
    children: hasChildren && Array.isArray(raw.children)
      ? raw.children.map(readSchemaNode).filter(Boolean)
      : [],
  };
}

function readSchemaItem(raw, folderId) {
  if (!raw?.id) return null;
  return {
    id: raw.id,
    name: raw.name || 'Schema',
    folder_id: folderId,
    sort_order: typeof raw.sort_order === 'number' ? raw.sort_order : 0,
    rootType: raw.root_type === 'array' || raw.rootType === 'array' ? 'array' : 'object',
    nodes: Array.isArray(raw.nodes) ? raw.nodes.map(readSchemaNode).filter(Boolean) : [],
  };
}

/* ── helpers ───────────────────────────────────────────────────────────────── */

async function ensureDirFs(absPath) {
  const fs = await fsModule();
  try {
    await fs.mkdir(absPath, { recursive: true });
  } catch {
    // ignore if exists
  }
}

/** Unique, clean slug within a sibling set (no id suffix; ids live in the YAML). */
function uniqueSlug(name, used, { reserved } = {}) {
  let base = slugify(name, '') || 'item';
  if (reserved && reserved.has(base)) base = `${base}-collection`;
  let slug = base;
  let n = 2;
  while (used.has(slug)) {
    slug = `${base}-${n}`;
    n += 1;
  }
  used.add(slug);
  return slug;
}

function normalizeVariable(v) {
  return {
    key: v.key ?? '',
    value: v.value ?? '',
    enabled: v.enabled !== false,
    ...(v.secret ? { secret: true } : {}),
  };
}

/**
 * A folder variable row for `.folder.yaml`. A `secret: true` row keeps its key
 * and flags but NOT its value (D5) — the mirror is a git-tracked tree, and a
 * secret folder value therefore does not survive a git round-trip.
 */
function mirrorFolderVariable(v) {
  const row = normalizeVariable(v);
  if (row.secret) {
    row.value = '';
    if (v.scriptAccess === true) row.scriptAccess = true;
  }
  return row;
}

function cleanKV(list) {
  return withoutBlankRows(list || []).map((x) => ({
    key: x.key ?? '',
    value: x.value ?? '',
    enabled: x.enabled !== false,
  }));
}

function isEmptyObject(o) {
  return !o || typeof o !== 'object' || Object.keys(o).length === 0;
}

/**
 * Curated, omit-empty request object for clean diffs. Fields are written in a
 * readable order (name → method → url → … → sort_order → id) and any empty /
 * default field is left out entirely.
 */
// The fields requestToObject writes under its own names; everything else passes through.
const NESTED_MAPPED_KEYS = new Set([
  'id', 'name', 'method', 'url', 'description', 'headers', 'params', 'cookies', 'body', 'body_type',
  'bodyType', 'auth', 'preRequestScript', 'pre_request_script', 'testScript', 'test_script', 'mock',
  'settings', 'sort_order', 'folder_id',
]);

function requestToObject(r) {
  // `type`, gRPC / WebSocket fields, … — the old field list turned a gRPC request
  // into an empty HTTP one on every pull (card 5258c71d).
  const o = { ...requestExtraFields(r, NESTED_MAPPED_KEYS) };
  o.name = r.name || 'request';
  o.method = r.method || 'GET';
  o.url = r.url ?? '';
  if (r.description) o.description = r.description;

  const headers = cleanKV(r.headers);
  if (headers.length) o.headers = headers;
  const params = cleanKV(r.params);
  if (params.length) o.params = params;
  const cookies = cleanKV(r.cookies);
  if (cookies.length) o.cookies = cookies;

  const body = r.body ?? '';
  if (body) {
    o.body_type = r.body_type ?? r.bodyType ?? 'json';
    o.body = body;
  }

  const auth = r.auth;
  if (auth && auth.type && auth.type !== 'noauth') o.auth = auth;

  const pre = r.preRequestScript ?? r.pre_request_script ?? '';
  if (pre) o.pre_request_script = pre;
  const test = r.testScript ?? r.test_script ?? '';
  if (test) o.test_script = test;

  const mock = normalizeMock(r.mock);
  if ((mock.responseExamples || []).length > 0) o.mock = mock;

  if (!isEmptyObject(r.settings)) o.settings = r.settings;

  // `rawBodyLanguage` is written only when it says something: the editor saves
  // its default ("json" with no body) and an imported request has none, which
  // showed as a change nobody made. Left out only if reading the file back
  // gives the same language (readRequestToModel: body_type defaults to json).
  if (o.rawBodyLanguage !== undefined && !(o.settings && o.settings.raw_body_language !== undefined)) {
    const readBack = normalizeRequestBodyMode({ body_type: o.body_type ?? 'json', settings: o.settings });
    if (readBack.rawBodyLanguage === o.rawBodyLanguage) delete o.rawBodyLanguage;
  }

  o.sort_order = typeof r.sort_order === 'number' ? r.sort_order : 0;
  o.id = r.id;
  return o;
}

/** Rehydrate a request read back from disk into the full in-memory model. */
function readRequestToModel(req, folderId) {
  const bodyType = req.body_type ?? 'json';
  const passthrough = {};
  // the same denylist as on write: a hand-edited file cannot set UI or server state
  for (const [key, value] of Object.entries(req || {})) if (!NESTED_MAPPED_KEYS.has(key) && !REQUEST_DENIED_KEYS.has(key)) passthrough[key] = value;
  return {
    ...passthrough,
    id: req.id,
    name: req.name || 'request',
    method: req.method || 'GET',
    url: req.url ?? '',
    description: req.description ?? '',
    headers: Array.isArray(req.headers) ? req.headers : [],
    params: Array.isArray(req.params) ? req.params : [],
    cookies: Array.isArray(req.cookies) ? req.cookies : [],
    body: req.body ?? '',
    body_type: bodyType,
    bodyType,
    auth: req.auth ?? { type: 'noauth' },
    settings: req.settings ?? {},
    preRequestScript: req.pre_request_script ?? '',
    testScript: req.test_script ?? '',
    pre_request_script: req.pre_request_script ?? '',
    test_script: req.test_script ?? '',
    mock: normalizeMock(req.mock),
    sort_order: typeof req.sort_order === 'number' ? req.sort_order : 0,
    folder_id: folderId,
  };
}

/* ── build the file map (relativePath -> content) ────────────────────────────── */

export function buildMirrorFiles(snapshot, meta = {}) {
  const files = {};
  const id = meta.id || snapshot.id || '';
  const name = meta.name || snapshot.workspaceName || 'Workspace';

  const manifest = { version: SCHEMA_VERSION, name };
  if (id) manifest.id = id;
  if (snapshot.selectedEnvironmentId) manifest.selected_environment = snapshot.selectedEnvironmentId;
  files['apiboo.yaml'] = mdump(manifest);

  // Apiboo's marked blocks only — the writer merges them into the user's own
  // files (schema.mergeManagedBlock). `*.local.yaml` (the secret sidecars
  // below) is ignored by it.
  files['.gitignore'] = GITIGNORE_TEMPLATE;
  files['.gitattributes'] = GITATTRIBUTES_TEMPLATE;

  // Secret values never go into a tracked file (mirrorSecrets.js, 02.10.2026).
  const withSidecar = (rel, rows, extra = {}) => {
    const { tracked, local } = splitSecrets((rows || []).map(normalizeVariable));
    files[rel] = mdump({ ...extra.head, variables: tracked, ...extra.tail });
    if (local.length) files[sidecarOf(rel)] = mdump({ ...(extra.sidecarHead || {}), variables: local });
  };
  withSidecar('variables/globals.yaml', snapshot.globalVariables);
  withSidecar('variables/workspace.yaml', snapshot.workspaceVariables);

  const envUsed = new Set();
  for (const env of snapshot.environments || []) {
    const slug = uniqueSlug(env.name, envUsed);
    withSidecar(`environments/${slug}.yaml`, env.variables, {
      head: { name: env.name || slug },
      tail: { id: env.id },
      sidecarHead: { id: env.id },
    });
  }

  const collections = (snapshot.collections || [])
    .slice()
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  const colUsed = new Set();
  for (const col of collections) {
    const colSlug = uniqueSlug(col.name, colUsed, { reserved: RESERVED_TOP_LEVEL });
    const settings = { ...(col.settings || {}) };
    delete settings.folders; // folder tree is represented by real directories

    const colMeta = {};
    colMeta.name = col.name || colSlug;
    if (col.description) colMeta.description = col.description;
    if (!isEmptyObject(settings)) colMeta.settings = settings;
    colMeta.sort_order = typeof col.sort_order === 'number' ? col.sort_order : 0;
    colMeta.id = col.id;
    if (col.local_uid) colMeta.local_uid = col.local_uid;
    if (col.cloud_collection_id != null) colMeta.cloud_collection_id = col.cloud_collection_id;
    files[`${colSlug}/${COLLECTION_META}`] = mdump(colMeta);

    // Recurse the folder/request tree into real directories.
    const walk = (parentId, dirPath) => {
      const children = getOrderedChildren(col, parentId);
      const used = new Set([
        COLLECTION_META.replace(/\.yaml$/, ''),
        FOLDER_META.replace(/\.yaml$/, ''),
      ]);
      for (const child of children) {
        if (child.type === 'folder') {
          const fSlug = uniqueSlug(child.item.name, used);
          const fDir = `${dirPath}/${fSlug}`;
          const folderMeta = {
            name: child.item.name || fSlug,
            sort_order: typeof child.item.sort_order === 'number' ? child.item.sort_order : 0,
            id: child.item.id,
          };
          const folderVars = normalizeFolderVariables(child.item.variables).map(mirrorFolderVariable);
          if (folderVars.length) folderMeta.variables = folderVars;
          files[`${fDir}/${FOLDER_META}`] = mdump(folderMeta);
          walk(child.item.id, fDir);
        } else {
          const rSlug = uniqueSlug(child.item.name, used);
          files[`${dirPath}/${rSlug}.yaml`] = mdump(requestToObject(child.item));
        }
      }
    };
    walk(null, colSlug);
  }

  const schemaTree = snapshot.schemas || { folders: [], items: [] };
  const walkSchemas = (parentId, dirPath) => {
    const children = getOrderedSchemaChildren(schemaTree, parentId);
    const used = new Set([
      FOLDER_META.replace(/\.yaml$/, ''),
    ]);
    for (const child of children) {
      if (child.type === 'folder') {
        const fSlug = uniqueSlug(child.item.name, used);
        const fDir = `${dirPath}/${fSlug}`;
        files[`${fDir}/${FOLDER_META}`] = mdump({
          name: child.item.name || fSlug,
          sort_order: typeof child.item.sort_order === 'number' ? child.item.sort_order : 0,
          id: child.item.id,
        });
        walkSchemas(child.item.id, fDir);
      } else {
        const sSlug = uniqueSlug(child.item.name, used);
        files[`${dirPath}/${sSlug}.yaml`] = mdump(schemaItemToObject(child.item));
      }
    }
  };
  walkSchemas(null, 'schemas');

  return files;
}

/* ── write ───────────────────────────────────────────────────────────────────── */

/** Remove all mirror content except the .git directory (so git history survives). */
/** Top-level folders of the nested layout that hold a collection (from `files`). */
function collectionDirsIn(files) {
  return Object.keys(files)
    .filter((rel) => rel.split('/').length === 2 && rel.endsWith(`/${COLLECTION_META}`))
    .map((rel) => rel.split('/')[0]);
}

/** Top-level folders on disk that hold a collection — removed ones are orphans to clean. */
async function collectionDirsOnDisk(fs, baseDir) {
  const out = [];
  let entries = [];
  try {
    entries = await fs.readDir(baseDir);
  } catch {
    return out;
  }
  for (const e of entries) {
    if (!e?.isDirectory || !e.name || e.name.startsWith('.') || RESERVED_TOP_LEVEL.has(e.name)) continue;
    try {
      if (await fs.exists(`${baseDir}/${e.name}/${COLLECTION_META}`)) out.push(e.name);
    } catch {
      /* skip */
    }
  }
  return out;
}


/**
 * Write the workspace's in-memory snapshot to its YAML mirror as a nested tree.
 * Wipes stale content first so deletions/renames/moves are reflected exactly.
 *
 * @param {object} workspace - { id, name, storage }
 * @param {object} snapshot  - { workspaceName, collections, globalVariables, workspaceVariables, environments, selectedEnvironmentId }
 */
export async function writeMirror(workspace, snapshot) {
  if (!isTauri()) return;
  const id = workspace?.id;
  if (!id) return;
  // git-storage workspaces already persist their own YAML tree at rootPath.
  if (workspace?.storage === 'git') return;

  try {
    const normalized = normalizeSnapshot(snapshot);
    const meta = { id: String(id), name: workspace?.name || snapshot?.workspaceName || '' };
    const files = buildMirrorFiles(normalized, meta);

    const baseDir = (await mirrorAbsDir(id))?.replace(/\\/g, '/');
    if (!baseDir) return;
    const fs = await fsModule();
    await ensureDirFs(baseDir);
    // Before 02.10.2026 this emptied the folder (all but .git) and wrote
    // everything again — so a repo's own README, LICENSE or code was deleted
    // by every flush and the next sync pushed the deletion. Now: only changed
    // files are written, and only Apiboo's own files (the managed roots plus
    // every collection folder, before and after) can be removed as orphans.
    const roots = [...new Set([...MANAGED_ROOTS, ...(await collectionDirsOnDisk(fs, baseDir)), ...collectionDirsIn(files)])];
    await writeWorkspaceFiles(fs, baseDir, files, { roots });
  } catch (err) {
    console.error('[workspaceMirror] Failed to write mirror:', err);
  }
}

/* ── read (after a git pull/merge) ───────────────────────────────────────────── */

async function readYamlRel(absPath) {
  try {
    const fs = await fsModule();
    const text = await fs.readTextFile(absPath);
    return parseYaml(text);
  } catch {
    return null;
  }
}

async function readDirRel(absPath) {
  try {
    const fs = await fsModule();
    return await fs.readDir(absPath);
  } catch {
    return [];
  }
}

/** Parse the nested mirror tree back into an in-memory snapshot. */
export async function readMirror(workspaceId) {
  if (!isTauri() || !workspaceId) return normalizeSnapshot(null);
  const baseRel = await mirrorAbsDir(workspaceId);
  if (!baseRel) return normalizeSnapshot(null);

  const manifest = (await readYamlRel(`${baseRel}/apiboo.yaml`)) || {};
  // Tracked rows + the untracked secret sidecar next to them (mirrorSecrets.js).
  const withSecrets = async (rel) => {
    const doc = await readYamlRel(`${baseRel}/${rel}`);
    if (!doc) return null;
    const side = await readYamlRel(`${baseRel}/${sidecarOf(rel)}`);
    return { ...doc, variables: mergeSecrets(doc.variables || [], side?.variables || []) };
  };
  const globals = (await withSecrets('variables/globals.yaml')) || { variables: [] };
  const workspaceVars = (await withSecrets('variables/workspace.yaml')) || { variables: [] };

  // Environments (a `*.local.yaml` is a sidecar, not an environment)
  const environments = [];
  for (const entry of await readDirRel(`${baseRel}/environments`)) {
    if (!entry?.name?.endsWith('.yaml') || isSidecar(entry.name)) continue;
    const env = await withSecrets(`environments/${entry.name}`);
    if (env) environments.push(env);
  }

  // Collections (every top-level dir that isn't reserved / hidden)
  const collections = [];
  for (const entry of await readDirRel(baseRel)) {
    if (!entry?.isDirectory) continue;
    if (entry.name.startsWith('.')) continue;
    if (RESERVED_TOP_LEVEL.has(entry.name)) continue;

    const colDir = `${baseRel}/${entry.name}`;
    const colMeta = await readYamlRel(`${colDir}/${COLLECTION_META}`);
    if (!colMeta) continue;

    const folders = [];
    const requests = [];

    const walk = async (dirRel, parentFolderId) => {
      const items = await readDirRel(dirRel);
      for (const it of items) {
        if (!it?.name || it.name.startsWith('.')) continue;
        if (RESERVED_NAMES.has(it.name)) continue; // metadata files
        if (it.isDirectory) {
          const meta = await readYamlRel(`${dirRel}/${it.name}/${FOLDER_META}`);
          if (!meta?.id) continue;
          const folder = {
            id: meta.id,
            name: meta.name || it.name,
            parent_id: parentFolderId,
            sort_order: typeof meta.sort_order === 'number' ? meta.sort_order : 0,
          };
          const folderVars = normalizeFolderVariables(meta.variables);
          if (folderVars.length) folder.variables = folderVars;
          folders.push(folder);
          await walk(`${dirRel}/${it.name}`, meta.id);
        } else if (it.name.endsWith('.yaml')) {
          const req = await readYamlRel(`${dirRel}/${it.name}`);
          if (!req?.id) continue;
          requests.push(readRequestToModel(req, parentFolderId));
        }
      }
    };
    await walk(colDir, null);

    collections.push({
      id: colMeta.id,
      name: colMeta.name || entry.name,
      description: colMeta.description ?? '',
      sort_order: typeof colMeta.sort_order === 'number' ? colMeta.sort_order : 0,
      settings: { ...(colMeta.settings || {}), folders },
      requests,
      ...(colMeta.local_uid ? { local_uid: colMeta.local_uid } : {}),
      ...(colMeta.cloud_collection_id != null
        ? { cloud_collection_id: colMeta.cloud_collection_id }
        : {}),
    });
  }

  const schemas = { folders: [], items: [] };
  const schemasDir = `${baseRel}/schemas`;
  const walkSchemas = async (dirRel, parentFolderId) => {
    const items = await readDirRel(dirRel);
    for (const it of items) {
      if (!it?.name || it.name.startsWith('.')) continue;
      if (RESERVED_NAMES.has(it.name)) continue;
      if (it.isDirectory) {
        const meta = await readYamlRel(`${dirRel}/${it.name}/${FOLDER_META}`);
        if (!meta?.id) continue;
        schemas.folders.push({
          id: meta.id,
          name: meta.name || it.name,
          parent_id: parentFolderId,
          sort_order: typeof meta.sort_order === 'number' ? meta.sort_order : 0,
        });
        await walkSchemas(`${dirRel}/${it.name}`, meta.id);
      } else if (it.name.endsWith('.yaml')) {
        const raw = await readYamlRel(`${dirRel}/${it.name}`);
        const item = readSchemaItem(raw, parentFolderId);
        if (item) schemas.items.push(item);
      }
    }
  };
  const schemaEntries = await readDirRel(schemasDir);
  if (schemaEntries.length > 0) {
    await walkSchemas(schemasDir, null);
  }

  return normalizeSnapshot({
    version: SCHEMA_VERSION,
    id: manifest.id || String(workspaceId),
    workspaceName: manifest.name || '',
    selectedEnvironmentId: manifest.selected_environment || '',
    globalVariables: globals.variables || [],
    workspaceVariables: workspaceVars.variables || [],
    environments,
    collections,
    schemas,
  });
}

/** Remove a workspace's mirror folder (e.g. when the workspace is deleted). */
export async function removeMirror(workspaceId) {
  if (!isTauri() || !workspaceId) return;
  try {
    const dir = await mirrorAbsDir(workspaceId);
    if (!dir) return;
    const fs = await fsModule();
    await fs.remove(dir, { recursive: true });
  } catch {
    // ignore
  }
}
